import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './ClassWork.css';
import Sidebar from './Sidebar';
import Header from './Header';
import ConfirmationModal from './ConfirmationModal';
import AssignmentDialog from './AssignmentDialog';
import AssignmentViewEditDialog from './AssignmentViewEditDialog';
import TailoredVersionsDialog from './TailoredVersionsDialog';
import QuizDialog from './QuizDialog';
import Gradebook from './Gradebook';
import DocumentCameraCapture from './DocumentCameraCapture';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  Button,
  Badge,
  useMediaQuery,
  Typography,
  Divider,
  IconButton,
  Checkbox,
  Menu,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  AutoFixHigh as TailoredIcon,
  CameraAlt as CameraIcon,
  OpenInNew as PreviewIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Description as DocumentIcon,
  Link as LinkIcon,
  Quiz as QuizIcon,
  Grade as GradeIcon,
  Grading as MarkingIcon,
} from '@mui/icons-material';

interface Document {
  id: number;
  name: string;
  original_filename: string;
  file_extension: string;
  file_size: number;
  mime_type: string;
  owner_name: string;
  description?: string;
  reading_level?: number;
  language: string;
  grade_level?: string;
  subject?: string;
  created_at: string;
  updated_at: string;
  assignments_count: number;
  tailored_documents_count: number;
}

const ClassWork: React.FC = () => {
  const { t } = useTranslation();
  const { token, isAuthenticated, isLoading: authLoading } = useAuth();
  const isMobile = useMediaQuery('(max-width: 1159px)');
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Top-level tab: 0 = Documents, 1 = Grades — auto-switch via ?tab=grades
  const [mainTabIndex, setMainTabIndex] = useState(() =>
    searchParams.get('tab') === 'grades' ? 1 : 0
  );
  
  // View toggle: 'list' or 'card' - default to list on desktop, card on mobile
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name'>('newest');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set());
  
  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuDocument, setActionMenuDocument] = useState<Document | null>(null);
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [tailoredVersionsDialogOpen, setTailoredVersionsDialogOpen] = useState(false);
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  
  // Form states
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadReadingLevel, setUploadReadingLevel] = useState<number | ''>('');
  const [uploadLanguage, setUploadLanguage] = useState('en');
  const [uploadGradeLevel, setUploadGradeLevel] = useState('');
  const [uploadSubject, setUploadSubject] = useState('');
  // URL import states
  const [uploadTabIndex, setUploadTabIndex] = useState(0);
  const [importUrl, setImportUrl] = useState('');
  const [importUrlName, setImportUrlName] = useState('');
  const [importUrlDescription, setImportUrlDescription] = useState('');
  const [importUrlReadingLevel, setImportUrlReadingLevel] = useState<number | ''>('');
  const [importUrlLanguage, setImportUrlLanguage] = useState('en');
  const [importUrlGradeLevel, setImportUrlGradeLevel] = useState('');
  const [importUrlSubject, setImportUrlSubject] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    document: Document | null;
  }>({ isOpen: false, document: null });
  
  // Auto switch to card view on mobile
  useEffect(() => {
    if (isMobile) {
      setViewMode('card');
    }
  }, [isMobile]);

  useEffect(() => {
    if (token && isAuthenticated) {
      fetchDocuments();
    }
  }, [token, isAuthenticated]);
  
  // Extract unique grade levels and subjects for filters
  const uniqueGrades = useMemo(() => {
    const grades = documents
      .map(doc => doc.grade_level)
      .filter((grade): grade is string => Boolean(grade));
    return Array.from(new Set(grades)).sort();
  }, [documents]);
  
  const uniqueSubjects = useMemo(() => {
    const subjects = documents
      .map(doc => doc.subject)
      .filter((subject): subject is string => Boolean(subject));
    return Array.from(new Set(subjects)).sort();
  }, [documents]);
  
  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let result = [...documents];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(doc => 
        doc.name.toLowerCase().includes(query) ||
        doc.original_filename.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.subject?.toLowerCase().includes(query) ||
        doc.grade_level?.toLowerCase().includes(query)
      );
    }
    
    // Grade filter
    if (gradeFilter !== 'all') {
      result = result.filter(doc => doc.grade_level === gradeFilter);
    }
    
    // Subject filter
    if (subjectFilter !== 'all') {
      result = result.filter(doc => doc.subject === subjectFilter);
    }
    
    // Sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }
    
    return result;
  }, [documents, searchQuery, sortBy, gradeFilter, subjectFilter]);

  const fetchDocuments = async () => {
    if (!token || !isAuthenticated) return;
    
    try {
      setLoading(true);
      const response = await fetch('/api/documents', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(t('classWork.errors.fetchFailed'));
      }
      
      const data = await response.json();
      if (data.success) {
        setDocuments(data.documents);
      } else {
        throw new Error(data.error || t('classWork.errors.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setError(t('classWork.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile || !token || !isAuthenticated) return;

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName || uploadFile.name);
      formData.append('description', uploadDescription);
      if (uploadReadingLevel) {
        formData.append('reading_level', uploadReadingLevel.toString());
      }
      formData.append('language', uploadLanguage);
      if (uploadGradeLevel) {
        formData.append('grade_level', uploadGradeLevel);
      }
      if (uploadSubject) {
        formData.append('subject', uploadSubject);
      }

      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setDocuments([data.document, ...documents]);
        setUploadDialogOpen(false);
        setUploadFile(null);
        setUploadName('');
        setUploadDescription('');
        setUploadReadingLevel('');
        setUploadLanguage('en');
        setUploadGradeLevel('');
        setUploadSubject('');
      } else {
        throw new Error(data.error || t('classWork.errors.uploadFailed'));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setError(t('classWork.errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleUrlImport = async () => {
    if (!importUrl || !token || !isAuthenticated) return;

    try {
      setImporting(true);
      setImportError(null);

      const payload: Record<string, any> = {
        url: importUrl,
        name: importUrlName,
        description: importUrlDescription,
        language: importUrlLanguage,
      };
      if (importUrlReadingLevel) payload.reading_level = importUrlReadingLevel;
      if (importUrlGradeLevel) payload.grade_level = importUrlGradeLevel;
      if (importUrlSubject) payload.subject = importUrlSubject;

      const response = await fetch('/api/documents/import-url', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setDocuments([data.document, ...documents]);
        setUploadDialogOpen(false);
        setImportUrl('');
        setImportUrlName('');
        setImportUrlDescription('');
        setImportUrlReadingLevel('');
        setImportUrlLanguage('en');
        setImportUrlGradeLevel('');
        setImportUrlSubject('');
        setImportError(null);
        setUploadTabIndex(0);
      } else {
        setImportError(data.error || t('classWork.errors.importFailed'));
      }
    } catch (error) {
      console.error('Error importing from URL:', error);
      setImportError(t('classWork.errors.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const handleAssignDocument = async (assignmentData: any) => {
    if (!selectedDocument || !token || !isAuthenticated) return;

    const response = await fetch(`/api/documents/${selectedDocument.id}/assign`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(assignmentData),
    });

    const data = await response.json();
    if (data.success) {
      fetchDocuments();
      
      // Show success message with Google Classroom link if available
      if (data.google_classroom_assignment?.alternateLink) {
        // You could show a success notification here with the Google Classroom link
        console.log('Google Classroom assignment created:', data.google_classroom_assignment.alternateLink);
      }
    } else {
      throw new Error(data.error || 'Assignment failed');
    }
  };

  const handleDownload = async (document: Document) => {
    if (!token || !isAuthenticated) return;
    
    try {
      const response = await fetch(`/api/documents/${document.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success && data.download_url) {
        window.open(data.download_url, '_blank');
      } else {
        throw new Error(data.error || t('classWork.errors.downloadFailed'));
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setError(t('classWork.errors.downloadFailed'));
    }
  };

  const handleDeleteDocumentClick = (document: Document) => {
    setDeleteConfirmModal({ isOpen: true, document });
  };

  const handleDeleteDocument = async () => {
    const document = deleteConfirmModal.document;
    if (!document) return;
    
    setDeleteConfirmModal({ isOpen: false, document: null });
    
    if (!token || !isAuthenticated) return;

    try {
      const response = await fetch(`/api/documents/${document.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setDocuments(documents.filter(doc => doc.id !== document.id));
      } else {
        throw new Error(data.error || t('classWork.errors.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(t('classWork.errors.deleteFailed'));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };
  
  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDocuments(new Set(filteredDocuments.map(doc => doc.id)));
    } else {
      setSelectedDocuments(new Set());
    }
  };
  
  const handleSelectDocument = (docId: number, checked: boolean) => {
    const newSelected = new Set(selectedDocuments);
    if (checked) {
      newSelected.add(docId);
    } else {
      newSelected.delete(docId);
    }
    setSelectedDocuments(newSelected);
  };
  
  // Action menu handlers
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, document: Document) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuDocument(document);
  };
  
  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuDocument(null);
  };

  // Handle camera capture
  const handleCameraCapture = (file: File) => {
    setUploadFile(file);
    if (!uploadName) {
      // Generate a name from the file or use timestamp
      const timestamp = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      setUploadName(`Scanned Document - ${timestamp}`);
    }
    setCameraDialogOpen(false);
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="loading-state">
              <LinearProgress />
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="error-state">Please log in to access Classwork.</div>
          </div>
        </div>
      </div>
    );
  }

  // Loading documents
  if (loading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="loading-state">
              <LinearProgress />
              <p>Loading documents...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="class-work-section">
              {error && (
                <div className="error-alert">
                  <Alert 
                    severity="error" 
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                </div>
              )}

              {/* Page Header */}
              <div className="page-header">
                <div className="page-header-content">
                  <h1>{t('sidebar.classWork')}</h1>
                  <p>{t('classWork.pageDescription', 'Upload, manage, and assign documents to your students. Create tailored versions for individual student needs.')}</p>
                </div>
                {mainTabIndex === 0 && (
                  <div className="header-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => setUploadDialogOpen(true)}
                      aria-label={t('classWork.addDocument', 'Add Document')}
                    >
                      <CloudUploadIcon />
                      {t('classWork.addDocument', 'Add Document')}
                    </button>
                  </div>
                )}
              </div>

              {/* Top-level Tabs: Documents / Grades */}
              <Tabs
                value={mainTabIndex}
                onChange={(_, v) => setMainTabIndex(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                aria-label={t('classWork.tabs.ariaLabel', 'Classwork sections')}
              >
                <Tab
                  icon={<DocumentIcon />}
                  iconPosition="start"
                  label={t('classWork.tabs.documents', 'Documents')}
                  aria-label={t('classWork.tabs.documents', 'Documents')}
                />
                <Tab
                  icon={<GradeIcon />}
                  iconPosition="start"
                  label={t('classWork.tabs.grades', 'Grades')}
                  aria-label={t('classWork.tabs.grades', 'Grades')}
                />
              </Tabs>

              {/* Grades Tab */}
              {mainTabIndex === 1 && (
                <Gradebook initialClassId={searchParams.get('classId') ? Number(searchParams.get('classId')) : undefined} />
              )}

              {/* Documents Tab Content */}
              {mainTabIndex === 0 && <>

              {/* Documents Toolbar */}
              {documents.length > 0 && (
                <div className="documents-toolbar">
                  <div className="toolbar-left">
                    <div className="search-box">
                      <SearchIcon className="search-icon" />
                      <input
                        type="text"
                        placeholder={t('classWork.searchDocuments', 'Search documents...')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label={t('classWork.searchDocuments', 'Search documents')}
                      />
                    </div>
                    
                    <FormControl size="small" className="toolbar-select">
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
                        aria-label={t('classWork.sortBy', 'Sort by')}
                        displayEmpty
                      >
                        <MenuItem value="newest">{t('classWork.sortNewest', 'Newest')}</MenuItem>
                        <MenuItem value="oldest">{t('classWork.sortOldest', 'Oldest')}</MenuItem>
                        <MenuItem value="name">{t('classWork.sortName', 'Name')}</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  
                  <div className="toolbar-right">
                    <FormControl size="small" className="toolbar-filter">
                      <Select
                        value={gradeFilter}
                        onChange={(e) => setGradeFilter(e.target.value)}
                        displayEmpty
                        startAdornment={<FilterIcon className="filter-icon" />}
                        aria-label={t('classWork.filterByGrade', 'Filter by grade')}
                      >
                        <MenuItem value="all">{t('classWork.allGrades', 'All Grades')}</MenuItem>
                        {uniqueGrades.map(grade => (
                          <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small" className="toolbar-filter">
                      <Select
                        value={subjectFilter}
                        onChange={(e) => setSubjectFilter(e.target.value)}
                        displayEmpty
                        startAdornment={<FilterIcon className="filter-icon" />}
                        aria-label={t('classWork.filterBySubject', 'Filter by subject')}
                      >
                        <MenuItem value="all">{t('classWork.allSubjects', 'All Subjects')}</MenuItem>
                        {uniqueSubjects.map(subject => (
                          <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <div className="view-toggle">
                      <Tooltip title={t('classWork.listView', 'List view')}>
                        <IconButton
                          className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                          onClick={() => setViewMode('list')}
                          aria-label={t('classWork.listView', 'List view')}
                          aria-pressed={viewMode === 'list'}
                        >
                          <ViewListIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('classWork.cardView', 'Card view')}>
                        <IconButton
                          className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                          onClick={() => setViewMode('card')}
                          aria-label={t('classWork.cardView', 'Card view')}
                          aria-pressed={viewMode === 'card'}
                        >
                          <ViewModuleIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents Grid or Empty State */}
              {documents.length === 0 ? (
                <div className="empty-state">
                  <CloudUploadIcon className="empty-state-icon" />
                  <h3>{t('classWork.noDocuments', 'No Documents Yet')}</h3>
                  <p>{t('classWork.noDocumentsDescription', 'Start by uploading your first document. You can then assign it to students and create tailored versions.')}</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setUploadDialogOpen(true)}
                    aria-label={t('classWork.addFirstDocument', 'Add Your First Document')}
                  >
                    <CloudUploadIcon />
                    {t('classWork.addFirstDocument', 'Add Your First Document')}
                  </button>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="empty-state">
                  <SearchIcon className="empty-state-icon" />
                  <h3>{t('classWork.noResults', 'No Results Found')}</h3>
                  <p>{t('classWork.noResultsDescription', 'Try adjusting your search or filters to find what you\'re looking for.')}</p>
                </div>
              ) : viewMode === 'list' ? (
                /* List View */
                <div className="documents-list-container">
                  <table className="documents-list-table">
                    <thead>
                      <tr>
                        <th className="checkbox-cell">
                          <Checkbox
                            checked={selectedDocuments.size === filteredDocuments.length && filteredDocuments.length > 0}
                            indeterminate={selectedDocuments.size > 0 && selectedDocuments.size < filteredDocuments.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            aria-label={t('classWork.selectAll', 'Select all documents')}
                          />
                        </th>
                        <th className="document-cell">{t('classWork.document', 'Document')}</th>
                        <th className="tags-cell">{t('classWork.tags', 'Tags')}</th>
                        <th className="tailored-cell">{t('classWork.tailored', 'Tailored')}</th>
                        <th className="uploaded-cell">{t('classWork.uploaded', 'Uploaded')}</th>
                        <th className="actions-cell"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((document) => (
                        <tr 
                          key={document.id} 
                          className={`document-row ${selectedDocuments.has(document.id) ? 'selected' : ''}`}
                        >
                          <td className="checkbox-cell">
                            <Checkbox
                              checked={selectedDocuments.has(document.id)}
                              onChange={(e) => handleSelectDocument(document.id, e.target.checked)}
                              aria-label={t('classWork.selectDocument', 'Select {{name}}', { name: document.name })}
                            />
                          </td>
                          <td className="document-cell">
                            <div className="document-cell-content">
                              <div className="document-icon">
                                <DocumentIcon />
                              </div>
                              <div className="document-info">
                                <Tooltip title={document.name} placement="top-start" enterDelay={400}>
                                  <span className="document-name">{document.name}</span>
                                </Tooltip>
                              </div>
                            </div>
                          </td>
                          <td className="tags-cell">
                            <div className="document-tags">
                              {document.grade_level && (
                                <span className="tag tag-grade">{document.grade_level}</span>
                              )}
                              {document.subject && (
                                <span className="tag tag-subject">{document.subject}</span>
                              )}
                              <span className="tag tag-type">{document.file_extension.toUpperCase()}</span>
                            </div>
                          </td>
                          <td className="tailored-cell">
                            {document.tailored_documents_count > 0 ? (
                              <button
                                className="tailored-badge"
                                onClick={() => {
                                  setSelectedDocument(document);
                                  setTailoredVersionsDialogOpen(true);
                                }}
                                aria-label={t('classWork.viewTailoredVersions', 'View {{count}} tailored version(s)', { count: document.tailored_documents_count })}
                              >
                                {document.tailored_documents_count} {t('classWork.tailoredLabel', 'tailored')}
                              </button>
                            ) : (
                              <span className="no-tailored">—</span>
                            )}
                          </td>
                          <td className="uploaded-cell">
                            {formatDate(document.created_at)}
                          </td>
                          <td className="actions-cell">
                            <IconButton
                              size="small"
                              onClick={(e) => handleActionMenuOpen(e, document)}
                              aria-label={t('classWork.documentActions', 'Document actions for {{name}}', { name: document.name })}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Card View */
                <div className="documents-grid">
                  {filteredDocuments.map((document) => (
                    <div key={document.id} className="document-card">
                      <div className="document-card-header">
                        <Tooltip title={document.name} placement="top-start" enterDelay={400}>
                          <h3 className="document-card-title">{document.name}</h3>
                        </Tooltip>
                        <Tooltip title={document.original_filename} placement="top-start" enterDelay={400}>
                          <p className="document-card-filename">{document.original_filename}</p>
                        </Tooltip>
                        {document.description && (
                          <p className="document-card-description">{document.description}</p>
                        )}
                      </div>
                      
                      <div className="document-card-content">
                        <div className="document-card-meta">
                          <span className="document-chip">
                            {formatFileSize(document.file_size)}
                          </span>
                          <span className="document-chip">
                            {document.file_extension.toUpperCase()}
                          </span>
                          {document.reading_level && (
                            <span className="document-chip">
                              {t('classWork.level', 'Level')} {document.reading_level}
                            </span>
                          )}
                          {document.grade_level && (
                            <span className="document-chip">
                              {document.grade_level}
                            </span>
                          )}
                          {document.subject && (
                            <span className="document-chip">
                              {document.subject}
                            </span>
                          )}
                          {document.language !== 'en' && (
                            <span className="document-chip">
                              {document.language.toUpperCase()}
                            </span>
                          )}
                          {document.assignments_count > 0 && (
                            <span className="document-chip primary">
                              {document.assignments_count} {t('classWork.studentCount', 'student', { count: document.assignments_count })}{document.assignments_count !== 1 ? 's' : ''}
                            </span>
                          )}
                          {document.tailored_documents_count > 0 && (
                            <span className="document-chip secondary">
                              {document.tailored_documents_count} {t('classWork.tailoredLabel', 'tailored')}
                            </span>
                          )}
                        </div>
                        <p className="document-card-date">
                          {t('classWork.uploadedOn', 'Uploaded')} {formatDate(document.created_at)}
                        </p>
                      </div>
                      
                      <div className="document-card-actions">
                        <button
                          className="document-action-btn"
                          onClick={() => handleDownload(document)}
                          title={t('classWork.preview', 'Preview')}
                          aria-label={t('classWork.previewDocument', 'Preview {{name}} in new tab', { name: document.name })}
                        >
                          <PreviewIcon />
                        </button>
                        <button
                          className="document-action-btn"
                          onClick={() => handleDownload(document)}
                          title={t('classWork.download', 'Download')}
                          aria-label={t('classWork.downloadDocument', 'Download {{name}}', { name: document.name })}
                        >
                          <DownloadIcon />
                        </button>
                        <button
                          className="document-action-btn"
                          onClick={() => {
                            setSelectedDocument(document);
                            setAssignDialogOpen(true);
                          }}
                          title={t('classWork.assignToStudents')}
                          aria-label={t('classWork.assignDocument', 'Assign {{name}} to students', { name: document.name })}
                        >
                          <PersonAddIcon />
                        </button>
                        <button
                          className="document-action-btn tailored-btn"
                          onClick={() => {
                            setSelectedDocument(document);
                            if (document.tailored_documents_count > 0) {
                              setTailoredVersionsDialogOpen(true);
                            } else {
                              setAssignDialogOpen(true);
                            }
                          }}
                          title={document.tailored_documents_count > 0
                            ? t('classWork.viewTailoredVersions', 'View {{count}} tailored version(s)', { count: document.tailored_documents_count })
                            : t('classWork.tailorDocument', 'Tailor Document')}
                          aria-label={document.tailored_documents_count > 0
                            ? t('classWork.viewTailoredFor', 'View tailored versions for {{name}}', { name: document.name })
                            : t('classWork.tailorDocumentFor', 'Tailor {{name}} for students', { name: document.name })}
                        >
                          <Badge 
                            badgeContent={document.tailored_documents_count || 0} 
                            color="secondary"
                            invisible={document.tailored_documents_count === 0}
                            sx={{ 
                              '& .MuiBadge-badge': { 
                                fontSize: '0.65rem', 
                                minWidth: '16px', 
                                height: '16px',
                                top: -2,
                                right: -2
                              } 
                            }}
                          >
                            <TailoredIcon />
                          </Badge>
                        </button>
                        <button
                          className="document-action-btn"
                          onClick={() => {
                            setSelectedDocument(document);
                            setQuizDialogOpen(true);
                          }}
                          title={t('quiz.menuLabel', 'Comprehension Quiz')}
                          aria-label={t('quiz.menuLabelFor', 'Comprehension quiz for {{name}}', { name: document.name })}
                        >
                          <QuizIcon />
                        </button>
                        {document.assignments_count > 0 && (
                          <button
                            className="document-action-btn"
                            onClick={() => setMainTabIndex(1)}
                            title={t('classWork.markSubmissions', 'Mark Submissions')}
                            aria-label={t('classWork.markSubmissionsFor', 'Mark submissions for {{name}}', { name: document.name })}
                          >
                            <MarkingIcon />
                          </button>
                        )}
                        <button
                          className="document-action-btn"
                          onClick={() => {
                            setSelectedDocument(document);
                            setViewDialogOpen(true);
                          }}
                          title={t('classWork.viewAssignmentDetails')}
                          aria-label={t('classWork.viewAssignmentFor', 'View assignment details for {{name}}', { name: document.name })}
                        >
                          <VisibilityIcon />
                        </button>
                        <button
                          className="document-action-btn error"
                          onClick={() => handleDeleteDocumentClick(document)}
                          title={t('common.delete')}
                          aria-label={t('classWork.deleteDocument', 'Delete {{name}}', { name: document.name })}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Action Menu for List View */}
              <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={handleActionMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={() => {
                  if (actionMenuDocument) handleDownload(actionMenuDocument);
                  handleActionMenuClose();
                }}>
                  <PreviewIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('classWork.preview', 'Preview')}
                </MenuItem>
                <MenuItem onClick={() => {
                  if (actionMenuDocument) handleDownload(actionMenuDocument);
                  handleActionMenuClose();
                }}>
                  <DownloadIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('classWork.download', 'Download')}
                </MenuItem>
                <MenuItem onClick={() => {
                  if (actionMenuDocument) {
                    setSelectedDocument(actionMenuDocument);
                    setAssignDialogOpen(true);
                  }
                  handleActionMenuClose();
                }}>
                  <PersonAddIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('classWork.assignToStudents', 'Assign to Students')}
                </MenuItem>
                <MenuItem onClick={() => {
                  if (actionMenuDocument) {
                    setSelectedDocument(actionMenuDocument);
                    setAssignDialogOpen(true);
                  }
                  handleActionMenuClose();
                }}>
                  <TailoredIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('classWork.tailorDocument', 'Tailor Document')}
                </MenuItem>
                {(actionMenuDocument?.tailored_documents_count ?? 0) > 0 && (
                  <MenuItem onClick={() => {
                    if (actionMenuDocument) {
                      setSelectedDocument(actionMenuDocument);
                      setTailoredVersionsDialogOpen(true);
                    }
                    handleActionMenuClose();
                  }}>
                    <VisibilityIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    {t('classWork.viewTailoredVersions', 'View {{count}} Tailored Version(s)', { count: actionMenuDocument?.tailored_documents_count })}
                  </MenuItem>
                )}
                <MenuItem onClick={() => {
                  if (actionMenuDocument) {
                    setSelectedDocument(actionMenuDocument);
                    setQuizDialogOpen(true);
                  }
                  handleActionMenuClose();
                }}>
                  <QuizIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('quiz.menuLabel', 'Comprehension Quiz')}
                </MenuItem>
                {(actionMenuDocument?.assignments_count ?? 0) > 0 && (
                  <MenuItem onClick={() => {
                    setMainTabIndex(1);
                    handleActionMenuClose();
                  }}>
                    <MarkingIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    {t('classWork.markSubmissions', 'Mark Submissions')}
                  </MenuItem>
                )}
                <MenuItem onClick={() => {
                  if (actionMenuDocument) {
                    setSelectedDocument(actionMenuDocument);
                    setViewDialogOpen(true);
                  }
                  handleActionMenuClose();
                }}>
                  <VisibilityIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('classWork.viewAssignmentDetails', 'View Assignment Details')}
                </MenuItem>
                <Divider />
                <MenuItem 
                  onClick={() => {
                    if (actionMenuDocument) handleDeleteDocumentClick(actionMenuDocument);
                    handleActionMenuClose();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} />
                  {t('common.delete', 'Delete')}
                </MenuItem>
              </Menu>

              </>}
            </div>
          </div>
        </div>
      </div>

      {/* Upload / Import Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => { setUploadDialogOpen(false); setImportError(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('classWork.addDocument', 'Add Document')}</DialogTitle>
        <DialogContent>
          <Tabs
            value={uploadTabIndex}
            onChange={(_, v) => { setUploadTabIndex(v); setImportError(null); }}
            variant="fullWidth"
            sx={{ mb: 2 }}
            aria-label={t('classWork.uploadMethodTabs', 'Choose upload method')}
          >
            <Tab
              icon={<UploadIcon />}
              iconPosition="start"
              label={t('classWork.fileUploadTab', 'File Upload')}
              aria-label={t('classWork.fileUploadTab', 'File Upload')}
            />
            <Tab
              icon={<LinkIcon />}
              iconPosition="start"
              label={t('classWork.urlImportTab', 'Import from URL')}
              aria-label={t('classWork.urlImportTab', 'Import from URL')}
            />
          </Tabs>

          {/* =================== FILE UPLOAD TAB =================== */}
          {uploadTabIndex === 0 && (
            <Box sx={{ mt: 1 }}>
              {isMobile && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<CameraIcon />}
                    fullWidth
                    onClick={() => setCameraDialogOpen(true)}
                    sx={{
                      mb: 2, p: 2,
                      backgroundColor: '#2563eb',
                      '&:hover': { backgroundColor: '#1d4ed8' }
                    }}
                    aria-label={t('documentCamera.scanWithCamera')}
                  >
                    {t('documentCamera.scanWithCamera')}
                  </Button>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">{t('common.or')}</Typography>
                  </Divider>
                </>
              )}

              <input
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setUploadFile(file);
                    if (!uploadName) setUploadName(file.name.replace(/\.[^/.]+$/, ""));
                  }
                }}
                style={{ display: 'none' }}
                id="file-upload-input"
                accept=".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.csv,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.bmp,.mp4,.avi,.mov,.wmv"
                aria-label={t('documentCamera.chooseFile')}
              />
              <label htmlFor="file-upload-input">
                <Button variant="outlined" component="span" startIcon={<UploadIcon />} fullWidth sx={{ mb: 2, p: 2 }}>
                  {uploadFile ? uploadFile.name : t('documentCamera.chooseFile')}
                </Button>
              </label>

              <TextField
                label={t('classWork.documentName', 'Document Name')}
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                fullWidth margin="normal" required
                aria-label={t('classWork.documentName', 'Document Name')}
              />
              <TextField
                label={t('classWork.descriptionOptional', 'Description (Optional)')}
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                fullWidth margin="normal" multiline rows={3}
                aria-label={t('classWork.descriptionOptional', 'Description (Optional)')}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  label={t('classWork.readingLevel')}
                  type="number"
                  value={uploadReadingLevel}
                  onChange={(e) => setUploadReadingLevel(e.target.value ? parseInt(e.target.value) : '')}
                  inputProps={{ min: 0 }}
                  helperText={t('classWork.lexileScale')}
                  sx={{ flex: 1 }}
                  aria-label={t('classWork.readingLevel')}
                />
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('classWork.language')}</InputLabel>
                  <Select value={uploadLanguage} onChange={(e) => setUploadLanguage(e.target.value)} label={t('classWork.language')} aria-label={t('classWork.language')}>
                    <MenuItem value="en">{t('classWork.languages.english')}</MenuItem>
                    <MenuItem value="es">{t('classWork.languages.spanish')}</MenuItem>
                    <MenuItem value="fr">{t('classWork.languages.french')}</MenuItem>
                    <MenuItem value="de">{t('classWork.languages.german')}</MenuItem>
                    <MenuItem value="it">{t('classWork.languages.italian')}</MenuItem>
                    <MenuItem value="pt">{t('classWork.languages.portuguese')}</MenuItem>
                    <MenuItem value="zh">{t('classWork.languages.chinese')}</MenuItem>
                    <MenuItem value="ja">{t('classWork.languages.japanese')}</MenuItem>
                    <MenuItem value="ko">{t('classWork.languages.korean')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('classWork.gradeLevel', 'Grade Level')}</InputLabel>
                  <Select value={uploadGradeLevel} onChange={(e) => setUploadGradeLevel(e.target.value)} label={t('classWork.gradeLevel', 'Grade Level')} aria-label={t('classWork.gradeLevel', 'Grade Level')}>
                    <MenuItem value="">{t('classWork.notSpecified', 'Not Specified')}</MenuItem>
                    <MenuItem value="K">Kindergarten</MenuItem>
                    <MenuItem value="1st">1st Grade</MenuItem>
                    <MenuItem value="2nd">2nd Grade</MenuItem>
                    <MenuItem value="3rd">3rd Grade</MenuItem>
                    <MenuItem value="4th">4th Grade</MenuItem>
                    <MenuItem value="5th">5th Grade</MenuItem>
                    <MenuItem value="6th">6th Grade</MenuItem>
                    <MenuItem value="7th">7th Grade</MenuItem>
                    <MenuItem value="8th">8th Grade</MenuItem>
                    <MenuItem value="9th">9th Grade</MenuItem>
                    <MenuItem value="10th">10th Grade</MenuItem>
                    <MenuItem value="11th">11th Grade</MenuItem>
                    <MenuItem value="12th">12th Grade</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={t('classWork.subject', 'Subject')}
                  value={uploadSubject}
                  onChange={(e) => setUploadSubject(e.target.value)}
                  placeholder={t('classWork.subjectPlaceholder', 'e.g., Math, Science, English')}
                  sx={{ flex: 1 }}
                  aria-label={t('classWork.subject', 'Subject')}
                />
              </Box>
            </Box>
          )}

          {/* =================== URL IMPORT TAB =================== */}
          {uploadTabIndex === 1 && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('classWork.urlImportHint', 'Paste a URL to a web article, PDF, image, or Google Doc. The system will automatically detect the content type and import it.')}
              </Alert>

              {importError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError(null)}>
                  {importError}
                </Alert>
              )}

              <TextField
                label={t('classWork.urlLabel', 'URL')}
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                fullWidth
                margin="normal"
                required
                placeholder="https://example.com/article or https://docs.google.com/..."
                aria-label={t('classWork.urlLabel', 'URL')}
                InputProps={{
                  startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('classWork.urlSupportedTypes', 'Supported: Web pages, PDFs, images (PNG/JPG), Google Docs/Sheets/Slides')}
              </Typography>

              <TextField
                label={t('classWork.documentName', 'Document Name')}
                value={importUrlName}
                onChange={(e) => setImportUrlName(e.target.value)}
                fullWidth margin="normal"
                helperText={t('classWork.urlNameHint', 'Leave blank to auto-detect from the page title')}
                aria-label={t('classWork.documentName', 'Document Name')}
              />
              <TextField
                label={t('classWork.descriptionOptional', 'Description (Optional)')}
                value={importUrlDescription}
                onChange={(e) => setImportUrlDescription(e.target.value)}
                fullWidth margin="normal" multiline rows={2}
                aria-label={t('classWork.descriptionOptional', 'Description (Optional)')}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  label={t('classWork.readingLevel')}
                  type="number"
                  value={importUrlReadingLevel}
                  onChange={(e) => setImportUrlReadingLevel(e.target.value ? parseInt(e.target.value) : '')}
                  inputProps={{ min: 0 }}
                  helperText={t('classWork.lexileScale')}
                  sx={{ flex: 1 }}
                  aria-label={t('classWork.readingLevel')}
                />
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('classWork.language')}</InputLabel>
                  <Select value={importUrlLanguage} onChange={(e) => setImportUrlLanguage(e.target.value)} label={t('classWork.language')} aria-label={t('classWork.language')}>
                    <MenuItem value="en">{t('classWork.languages.english')}</MenuItem>
                    <MenuItem value="es">{t('classWork.languages.spanish')}</MenuItem>
                    <MenuItem value="fr">{t('classWork.languages.french')}</MenuItem>
                    <MenuItem value="de">{t('classWork.languages.german')}</MenuItem>
                    <MenuItem value="it">{t('classWork.languages.italian')}</MenuItem>
                    <MenuItem value="pt">{t('classWork.languages.portuguese')}</MenuItem>
                    <MenuItem value="zh">{t('classWork.languages.chinese')}</MenuItem>
                    <MenuItem value="ja">{t('classWork.languages.japanese')}</MenuItem>
                    <MenuItem value="ko">{t('classWork.languages.korean')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>{t('classWork.gradeLevel', 'Grade Level')}</InputLabel>
                  <Select value={importUrlGradeLevel} onChange={(e) => setImportUrlGradeLevel(e.target.value)} label={t('classWork.gradeLevel', 'Grade Level')} aria-label={t('classWork.gradeLevel', 'Grade Level')}>
                    <MenuItem value="">{t('classWork.notSpecified', 'Not Specified')}</MenuItem>
                    <MenuItem value="K">Kindergarten</MenuItem>
                    <MenuItem value="1st">1st Grade</MenuItem>
                    <MenuItem value="2nd">2nd Grade</MenuItem>
                    <MenuItem value="3rd">3rd Grade</MenuItem>
                    <MenuItem value="4th">4th Grade</MenuItem>
                    <MenuItem value="5th">5th Grade</MenuItem>
                    <MenuItem value="6th">6th Grade</MenuItem>
                    <MenuItem value="7th">7th Grade</MenuItem>
                    <MenuItem value="8th">8th Grade</MenuItem>
                    <MenuItem value="9th">9th Grade</MenuItem>
                    <MenuItem value="10th">10th Grade</MenuItem>
                    <MenuItem value="11th">11th Grade</MenuItem>
                    <MenuItem value="12th">12th Grade</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={t('classWork.subject', 'Subject')}
                  value={importUrlSubject}
                  onChange={(e) => setImportUrlSubject(e.target.value)}
                  placeholder={t('classWork.subjectPlaceholder', 'e.g., Math, Science, English')}
                  sx={{ flex: 1 }}
                  aria-label={t('classWork.subject', 'Subject')}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setImportError(null); }}>
            {t('common.cancel')}
          </Button>
          {uploadTabIndex === 0 ? (
            <Button
              onClick={handleFileUpload}
              variant="contained"
              disabled={!uploadFile || uploading}
              aria-label={t('classWork.uploadDocument')}
            >
              {uploading ? t('classWork.uploading', 'Uploading...') : t('classWork.uploadDocument')}
            </Button>
          ) : (
            <Button
              onClick={handleUrlImport}
              variant="contained"
              disabled={!importUrl || importing}
              startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
              aria-label={t('classWork.importFromUrl', 'Import from URL')}
            >
              {importing ? t('classWork.importing', 'Importing...') : t('classWork.importFromUrl', 'Import from URL')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <AssignmentDialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onAssign={handleAssignDocument}
      />

      {/* Assignment View/Edit Dialog */}
      <AssignmentViewEditDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onSave={() => {
          fetchDocuments();
        }}
        onOpenAssign={() => {
          setViewDialogOpen(false);
          setAssignDialogOpen(true);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteConfirmModal.document?.name}"?`}
        variant="danger"
        confirmText="Delete"
        onConfirm={handleDeleteDocument}
        onCancel={() => setDeleteConfirmModal({ isOpen: false, document: null })}
      />

      {/* Tailored Versions Dialog */}
      <TailoredVersionsDialog
        open={tailoredVersionsDialogOpen}
        onClose={() => {
          setTailoredVersionsDialogOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onDelete={() => {
          fetchDocuments(); // Refresh to update tailored count
        }}
      />

      {/* Quiz Dialog */}
      <QuizDialog
        open={quizDialogOpen}
        onClose={() => {
          setQuizDialogOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
      />

      {/* Document Camera Capture Dialog (Mobile) */}
      <DocumentCameraCapture
        open={cameraDialogOpen}
        onClose={() => setCameraDialogOpen(false)}
        onCapture={handleCameraCapture}
      />
    </>
  );
};

export default ClassWork;